import { Route } from 'vue-router';
import { useContext } from '@nuxtjs/composition-api';
import { FacetKey, Facets, isFacetKey } from './types';
import { Filters } from '~//composables/search/useFilters';
import { keys } from '~/utils/objects';
import { FormattedFacet } from '~/types/facets';

/**
 *
 * @returns a list of filters, mapped from the query string. This is used to populate any filters
 * that need to be applied from any server render
 */
export const mapQueryToFilters = (query: Route['query']): Filters => {
  if (!query || Object.entries(query).length === 0) {
    return {};
  }
  // Parse route into facets object
  const entries = Object.entries(query);
  return entries.reduce<Filters>((acc, [key, val]) => {
    if (isFacetKey(key)) {
      const value = Array.isArray(val) ? (val.filter(v => v) as string[]) : [val];
      return { ...acc, [key]: value };
    }

    return acc;
  }, {});
};

const priority: string[] = [
  'year-level-au.lvl0',
  'year-level-us.lvl0',
  'year-level-gb.lvl0',
  'filter-resource-type.lvl0',
  'filter-resource-type.lvl1',
  'postTypeTitle',
  'resource-type-au.lvl0',
  'resource-type-us.lvl0',
  'resource-type-gb.lvl0',
  'learning-area-au.lvl0',
  'learning-area-us.lvl0',
  'learning-area-gb.lvl0',
  'filter-learning-area.lvl0',
  'filter-learning-area.lvl1',
  'resource-availability.lvl0',
  'file-format.lvl0',
  'filter-attribute',
  'filter-price',
  'filter-format',
];

// function to wrap facets to delete values from the list if required
export const deleteFacetValues = (facets: Facets): Facets => {
  if (!facets) {
    return {};
  }

  const { $isFlagOn } = useContext();

  if ($isFlagOn('new-filter-attribute')) {
    delete facets['resource-availability.lvl0'];
    delete facets['resource-type-au.lvl0'];
    delete facets['resource-type-us.lvl0'];
    delete facets['resource-type-gb.lvl0'];
    delete facets['learning-area-au.lvl0'];
    delete facets['learning-area-us.lvl0'];
    delete facets['learning-area-gb.lvl0'];
    delete facets.postTypeTitle;
    delete facets['file-format.lvl0'];

    // temporary until we have a new UI for the new filter attribute
    delete facets['filter-resource-type.lvl1'];
    delete facets['filter-learning-area.lvl1'];
  } else {
    delete facets['filter-attribute'];
    delete facets['filter-resource-type.lvl0'];
    delete facets['filter-resource-type.lvl1'];
    delete facets['filter-learning-area.lvl0'];
    delete facets['filter-learning-area.lvl1'];
    delete facets['filter-price'];
    delete facets['filter-format'];
  }

  return facets;
};

export const filterFormattedFacetValues = (formattedFacets: FormattedFacet[]): FormattedFacet[] => {
  if (!formattedFacets) {
    return [];
  }

  const { $isFlagOn } = useContext();

  if ($isFlagOn('new-filter-attribute')) {
    return formattedFacets.filter(item => {
      return ![
        'resource-availability.lvl0',
        'resource-type-au.lvl0',
        'resource-type-us.lvl0',
        'resource-type-gb.lvl0',
        'learning-area-au.lvl0',
        'learning-area-us.lvl0',
        'learning-area-gb.lvl0',
        'file-format.lvl0',
        'postTypeTitle',
      ].includes(item.key);
    });
  }

  return formattedFacets.filter(item => {
    return ![
      'filter-format',
      'filter-price',
      'filter-resource-type.lvl0',
      'filter-resource-type.lvl1',
      'filter-learning-area.lvl0',
      'filter-learning-area.lvl1',
      'filter-attribute',
    ].includes(item.key);
  });
};
export const sortFormattedFacetValues = (formattedFacets: FormattedFacet[]): FormattedFacet[] => {
  return formattedFacets.sort((a, b) => priority.indexOf(a.key) - priority.indexOf(b.key));
};
export const sortFacetsBasedOnKey = (facets: Facets): Facets => {
  if (!facets) {
    return {};
  }
  const sortedFacets: Facets = sortObjectKeys(facets, (a, b) => {
    return priority.indexOf(a) - priority.indexOf(b);
  });

  keys<FacetKey>(sortedFacets).forEach(key => {
    if (key.includes('year-level')) {
      sortedFacets[key] = sortObjectKeys<FacetKey>(sortedFacets[key] || {}, (a, b) => {
        return a.localeCompare(b);
      });
    }
  });

  return sortedFacets;
};

type SortFn = (a: any, b: any) => number;
export const sortObjectKeys = <T extends string>(
  obj: { [key in T]?: any },
  sortFn: SortFn
): {
  [key in T]?: any;
} => {
  const sortedObj: { [key in T]?: any } = {};
  keys<T>(obj)
    .sort(sortFn)
    .forEach(key => {
      sortedObj[key] = obj[key];
    });
  return sortedObj;
};

const FacetLabelLookup = {
  learning_areas_label: { matchPattern: /learning-area-(au|gb|us).lvl0/, position: 1 },
  filter_learning_areas_label: { matchPattern: /filter-learning-area.lvl0/, position: 1 },
  grade_levels_label: { matchPattern: /year-level-(au|gb|us).lvl0/, position: 2 },
  resource_types_label: { matchPattern: /resource-type-(au|gb|us).lvl0/, position: 3 },
  type_of_resource_label: { matchPattern: /postTypeTitle/, position: 4 },
  availability_label: { matchPattern: /availability.lvl0/, position: 6 },
  file_format_label: { matchPattern: /file-format.lvl0/, position: 7 },
  publisher_label: { matchPattern: /publisher.name/, position: 8 },
  filter_attribute_label: { matchPattern: /filter-attribute/, position: 5 },
  filter_resource_type_label: { matchPattern: /filter-resource-type.lvl0/, position: 3 },
  filter_price_label: { matchPattern: /filter-price/, position: 10 },
  filter_format_label: { matchPattern: /filter-format/, position: 11 },
};

interface FacetDetails {
  label: string;
  order: number;
}

/**
 *
 * @returns an object that can be used for displaying a facet
 */
export const getFacetDetails = (facetKey: string): FacetDetails => {
  for (const [key, value] of Object.entries(FacetLabelLookup)) {
    if (facetKey.match(value.matchPattern)) {
      return { label: key, order: value.position };
    }
  }
  return { label: '', order: 0 };
};

export const getFacetsWithZeroValueThatHaveFilters = (facets: Facets, filters: Filters): Facets => {
  const facetsWithZeroValue: Facets = JSON.parse(JSON.stringify(facets || {}));
  if (!facetsWithZeroValue) {
    return {};
  }

  Object.entries(filters).forEach(([filterKey, filterVal]) => {
    if (!isFacetKey(filterKey)) {
      console.warn(`Filter key ${filterKey} is not a facet key`);
      return;
    }

    const facetValues = facets?.[filterKey];
    // if the key doesn't exist at all in the facets
    if (!facetValues) {
      facetsWithZeroValue[filterKey] = {};
    }

    filterVal?.forEach(facetValue => {
      facetsWithZeroValue[filterKey][facetValue] = facetsWithZeroValue[filterKey][facetValue] || 0;
    });
  });
  return facetsWithZeroValue;
};

// this is only used for mobile filters and should not include lvl1
export const getFacetsForLocale = (locale: string): Array<string> => {
  return [
    `learning-area-${locale}.lvl0`,
    'postTypeTitle',
    `year-level-${locale}.lvl0`,
    `resource-type-${locale}.lvl0`,
    'resource-availability.lvl0',
    'file-format.lvl0',
    'filter-attribute',
    'filter-resource-type.lvl0',
    'filter-learning-area.lvl0',
    'filter-format',
    'filter-price',
  ];
};

export const formatActiveFilters = (
  filters: Record<string, string[]>
): Record<string, string[]> => {
  const formattedActiveFilters: Record<string, string[]> = {};
  for (const key in filters) {
    formattedActiveFilters[getFacetDetails(key).label] = filters[key];
  }
  return formattedActiveFilters;
};
